import { useEffect, type SyntheticEvent } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
    Box,
    IconButton,
    Skeleton,
    Tab,
    Tabs,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import GavelIcon from '@mui/icons-material/Gavel';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { useNotificationSettings } from '../../notifications/hooks/useNotificationSettings';
import { useGetIdentity, useNotify } from 'react-admin';
import { SectionCard } from '../markets/components/shared';
import type { GateMode } from '../../types/gates';
import { useGateOperatorAccounts } from './hooks/useGateOperatorAccounts';
import { useGates, useGateAttentionCounts } from './hooks/useGates';
import { useGateMode } from './hooks/useGateMode';
import { GatesTable } from './GatesTable';
import { GateOperatorsPanel } from '../admin/GateOperatorsList';

type GatesViewTab = 'gates' | 'operators';

/**
 * GATE.md §3 transition semantics, surfaced next to the mode toggle.
 * `canChoose` distinguishes an operator who can create/reset the control
 * doc (invite the choice) from a read-only viewer (admin/non-operator) for
 * whom the null case is purely informational.
 */
function modeCaption(mode: GateMode | null, canChoose: boolean): string {
    if (mode === 'auto')
        return 'Auto — new markets auto-approve; review and Hold/Close below.';
    if (mode === 'gated') return 'Gated — new markets hold until you approve.';
    return canChoose ? 'No mode set — choose Auto or Gated' : 'Mode not set';
}

/**
 * /gates — master-detail like WatchlistView: left = assigned accounts with
 * needs-attention counts (the "account status"), right = the gates table for
 * the selected account. Deep-linkable via ?account= & ?market= (used by the
 * gate_request notification row).
 *
 * Admins may open the page too (rules give them read access) but the
 * decision controls are read-only unless they are an assigned operator —
 * assignment decides, not role. An admin with a gate_operators grant for
 * the selected account clears gates and toggles modes exactly like any
 * other operator; an unassigned admin stays read-only.
 *
 * Admin viewers additionally get an "Operators" tab (formerly the separate
 * /admin/gates page) for assigning gate operators per account. The active
 * tab is driven by ?view= so it's deep-linkable and survives the existing
 * ?account=/?market= params; non-admins never see the tab bar.
 */
export function GatesView() {
    const theme = useTheme();
    const notify = useNotify();
    const [searchParams, setSearchParams] = useSearchParams();
    const { identity } = useGetIdentity();
    const { accountIds, loading } = useGateOperatorAccounts();
    const attentionCounts = useGateAttentionCounts(accountIds);

    const selected = searchParams.get('account');
    const highlight = searchParams.get('market');
    const {
        gates,
        loading: gatesLoading,
        error: gatesError,
        sortContext,
    } = useGates(selected);
    const {
        mode,
        loading: modeLoading,
        setMode,
        saving: modeSaving,
    } = useGateMode(selected);

    // Auto-select: deep link wins; otherwise first assigned account.
    useEffect(() => {
        if (!selected && accountIds.length > 0) {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev);
                    next.set('account', accountIds[0]);
                    return next;
                },
                { replace: true }
            );
        }
    }, [selected, accountIds, setSearchParams]);

    // The viewer can clear gates only for accounts they operate. Admins who
    // are not operators for the selected account get a read-only table.
    const isOperatorForSelected =
        selected !== null && accountIds.includes(selected);
    const isAdmin = identity?.role === 'admin';

    // #118: whether the viewer muted gate notifications for the selected
    // account — drives the bell hint in the panel header.
    const { settings: notifSettings } = useNotificationSettings();
    const gateNotificationsMuted =
        selected !== null &&
        Array.isArray(notifSettings.muted_gate_accounts) &&
        notifSettings.muted_gate_accounts.includes(selected);

    // Tab is admin-only and driven by ?view=; non-admins (or a stray
    // ?view=operators on a non-admin's link) always land on Gates.
    const activeTab: GatesViewTab =
        isAdmin && searchParams.get('view') === 'operators'
            ? 'operators'
            : 'gates';
    const handleTabChange = (_: SyntheticEvent, next: GatesViewTab) => {
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);
            if (next === 'operators') params.set('view', 'operators');
            else params.delete('view');
            return params;
        });
    };

    // Only operators may flip the switch; the write itself is audited
    // (design/GATE.md §3). No optimistic local state — the toggle reflects
    // the live control-doc snapshot, so a rejected write simply never
    // changes and the switch stays put (GATE_UI.md §7).
    const handleModeChange = async (next: GateMode) => {
        if (!isOperatorForSelected || modeSaving) return;
        try {
            await setMode(next);
        } catch {
            notify('Failed to update mode', { type: 'error' });
        }
    };

    const selectAccount = (id: string) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('account', id);
            next.delete('market');
            return next;
        });
    };

    // Admins without assignments can still deep-link to any account they
    // were sent; they just cannot browse accounts they don't know about.
    const visibleAccounts =
        selected && !accountIds.includes(selected) && isAdmin
            ? [selected, ...accountIds]
            : accountIds;

    return (
        <Box>
            {isAdmin && (
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab label="Gates" value="gates" />
                    <Tab label="Operators" value="operators" />
                </Tabs>
            )}
            {activeTab === 'operators' ? (
                <GateOperatorsPanel />
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        p: 2,
                        alignItems: 'flex-start',
                    }}
                >
                    {/* Left: account status cards */}
                    <Box
                        sx={{
                            width: 300,
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <GavelIcon fontSize="small" /> Gates
                        </Typography>
                        {loading && (
                            <Skeleton variant="rectangular" height={72} />
                        )}
                        {!loading && visibleAccounts.length === 0 && (
                            <SectionCard>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ p: 1 }}
                                >
                                    No accounts assigned. An admin must assign
                                    you a trading account before you can clear
                                    gates.
                                </Typography>
                            </SectionCard>
                        )}
                        {visibleAccounts.map(id => {
                            const pending = attentionCounts[id] ?? 0;
                            const active = id === selected;
                            return (
                                <Box
                                    key={id}
                                    onClick={() => selectAccount(id)}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        border: `1px solid ${active ? theme.palette.primary.main : alpha(theme.palette.divider, 0.6)}`,
                                        bgcolor: active
                                            ? alpha(
                                                  theme.palette.primary.main,
                                                  0.06
                                              )
                                            : 'background.paper',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                            sx={{
                                                fontFamily: 'monospace',
                                                fontSize: 13,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {id}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                color:
                                                    pending > 0
                                                        ? theme.palette.warning
                                                              .main
                                                        : 'text.secondary',
                                            }}
                                        >
                                            {pending > 0
                                                ? `${pending} awaiting approval`
                                                : 'nothing awaiting'}
                                        </Typography>
                                    </Box>
                                    <ChevronRightIcon
                                        fontSize="small"
                                        sx={{ color: 'text.disabled' }}
                                    />
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Right: gates table for the selected account */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {selected ? (
                            <SectionCard>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                        px: 2,
                                        pt: 1.5,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontFamily: 'monospace' }}
                                    >
                                        {selected}
                                    </Typography>
                                    {/* #118: per-account notification state
                                for operators — links to the settings page
                                where the mute toggles live. */}
                                    {isOperatorForSelected && (
                                        <Tooltip
                                            title={
                                                gateNotificationsMuted
                                                    ? 'Notifications for this account are off — manage in notification settings'
                                                    : 'You get notified about approval requests for this account — manage in notification settings'
                                            }
                                        >
                                            <IconButton
                                                size="medium"
                                                component={RouterLink}
                                                to="/settings/notifications"
                                                sx={{ ml: 'auto' }}
                                            >
                                                {gateNotificationsMuted ? (
                                                    <NotificationsOffIcon
                                                        sx={{
                                                            fontSize: 24,
                                                            color: 'text.disabled',
                                                        }}
                                                    />
                                                ) : (
                                                    <NotificationsActiveIcon
                                                        sx={{
                                                            fontSize: 24,
                                                            color: 'text.secondary',
                                                        }}
                                                    />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {/* auto/gated mode toggle — only assigned
                                operators can flip it (admin or not); an
                                unassigned viewer (including a non-operator
                                admin) sees it disabled.
                                When mode is null (control doc absent, or an
                                unrecognized value), the group simply has no
                                selected button — but stays enabled for
                                operators so they can make the first write
                                (merge-creates the doc, GATE.md §3) or
                                explicitly re-set an unknown value
                                (GATE_UI.md §7, notes for R4). */}
                                    <ToggleButtonGroup
                                        size="small"
                                        value={mode}
                                        exclusive
                                        onChange={(
                                            _,
                                            next: GateMode | null
                                        ) => {
                                            if (next)
                                                void handleModeChange(next);
                                        }}
                                        disabled={
                                            !isOperatorForSelected ||
                                            modeSaving ||
                                            modeLoading
                                        }
                                    >
                                        <ToggleButton
                                            value="auto"
                                            sx={{
                                                px: 1.5,
                                                py: 0.25,
                                                fontSize: 11,
                                            }}
                                        >
                                            Auto
                                        </ToggleButton>
                                        <ToggleButton
                                            value="gated"
                                            sx={{
                                                px: 1.5,
                                                py: 0.25,
                                                fontSize: 11,
                                            }}
                                        >
                                            Gated
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        px: 2,
                                        pb: 0.5,
                                        color: 'text.secondary',
                                    }}
                                >
                                    {modeLoading
                                        ? ' '
                                        : modeCaption(
                                              mode,
                                              isOperatorForSelected
                                          )}
                                </Typography>
                                {gatesLoading ? (
                                    <Box sx={{ p: 2 }}>
                                        <Skeleton
                                            variant="rectangular"
                                            height={160}
                                        />
                                    </Box>
                                ) : gatesError ? (
                                    <Box
                                        sx={{
                                            p: 4,
                                            textAlign: 'center',
                                            color: theme.palette.warning.main,
                                        }}
                                    >
                                        <Typography variant="body2">
                                            You don&apos;t have access to this
                                            account&apos;s gates
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Ask an admin to assign you as an
                                            operator for this account.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <GatesTable
                                        gates={gates}
                                        readOnly={!isOperatorForSelected}
                                        highlightConditionId={highlight}
                                        mode={mode}
                                        sortContext={sortContext}
                                    />
                                )}
                            </SectionCard>
                        ) : (
                            <Box
                                sx={{
                                    p: 6,
                                    textAlign: 'center',
                                    color: 'text.secondary',
                                }}
                            >
                                <Typography variant="body2">
                                    Select an account to see its gates.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
}
